import { createFileRoute } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronsUpDown, Plus } from 'lucide-react';
import { ChangeEvent, Suspense, useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button/button';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { ViewContent, ViewHeader } from '@/components/view-layout';
import { WithAuthorization } from '@/components/with-authorization';
import { RecipeTagFilterButton } from '@/features/recipe-tags/components/recipe-tag-filter-button';
import { useDeleteRecipe } from '@/features/recipes/api/recipes-mutations';
import { useRecipes } from '@/features/recipes/api/recipes-queries';
import { AddRecipeModal } from '@/features/recipes/components/add-recipe-modal';
import { RecipeTableActions } from '@/features/recipes/components/recipe-table-actions';
import { Recipe } from '@/features/recipes/types/recipe-types';

const columnHelper = createColumnHelper<Recipe>();

const makeColumns = (onDelete?: (id: string) => void) => [
  columnHelper.accessor('title', {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Name
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) => info.getValue(),
    size: 40,
  }),
  columnHelper.accessor('yield', {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Yield
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) => info.getValue(),
    size: 15,
    minSize: 10,
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('price', {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Price
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) => info.getValue().toFixed(2) || '0.00',
    size: 20,
    enableGlobalFilter: false,
  }),
  columnHelper.accessor(
    () => {
      return 0; // TODO: Calculate margin
    },
    {
      id: 'margin',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="w-[110px] px-0 justify-start"
          >
            Margin
            <ChevronsUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      size: 20,
      enableGlobalFilter: false,
    },
  ),
  columnHelper.accessor((recipe) => recipe.tags, {
    id: 'tags',
    header: () => null,
    cell: () => null,
    enableHiding: false,
    size: 30,
    enableGlobalFilter: false,
    filterFn: (row, _, filterValue: string[]) => {
      if (filterValue.length === 0) return true;
      const tags = row.original.tags;
      return filterValue.some((tagId) => {
        return tags.includes(tagId);
      });
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      const recipe = row.original;
      return (
        <RecipeTableActions
          recipe={recipe}
          onDelete={onDelete ?? (() => {})} // eslint-disable-line @typescript-eslint/no-empty-function
        />
      );
    },
    size: 5,
    minSize: 5,
    enableSorting: false,
    enableHiding: false,
  }),
];

interface RecipesTableProps {
  searchQuery: string;
  selectedTags: string[];
}

function RecipesTable({ searchQuery, selectedTags }: RecipesTableProps) {
  const { tenantId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data: recipes } = useRecipes(tenantId);
  const deleteRecipe = useDeleteRecipe();

  const handleDelete = async (recipeId: string) => {
    try {
      await deleteRecipe.mutateAsync({ id: recipeId });
    } catch {
      // Handle error silently or show user notification
    }
  };

  const columns = makeColumns(handleDelete);

  const table = useReactTable({
    data: recipes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnVisibility: {
        tags: false,
      },
    },
  });

  useEffect(() => {
    table.setGlobalFilter(searchQuery);
  }, [searchQuery, table]);

  useEffect(() => {
    table.setColumnFilters([
      {
        id: 'tags',
        value: selectedTags,
      },
    ]);
  }, [table, selectedTags]);

  const handleRowClick = async (recipe: Recipe) => {
    if (!recipe.id) return; // Skip if no ID

    await navigate({
      to: '/$tenantId/recipes/$recipeId',
      params: (prev) => ({
        ...prev,
        recipeId: recipe.id,
      }),
    });
  };

  return (
    <DataTable
      table={table}
      loading={false}
      skeletonRows={5}
      onClick={handleRowClick}
    />
  );
}

function RecipesRouteComponent() {
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = Route.useNavigate();
  const { search, tags } = Route.useSearch();

  const columns = makeColumns();

  const table = useReactTable({
    data: [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleSearchChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      await navigate({
        search: (prev) => ({ ...prev, search: e.target.value }),
      });
    },
    [navigate],
  );

  const handleSelectedTagsChange = useCallback(
    async (selectedTags: string[]) => {
      await navigate({
        search: (prev) => ({ ...prev, tags: selectedTags }),
      });
    },
    [navigate],
  );

  return (
    <>
      <ViewHeader title="Recipes">
        <div className="flex gap-4 items-center">
          <RecipeTagFilterButton
            selectedTagIds={tags}
            onSelectedTagIdsChange={handleSelectedTagsChange}
          />
          <Input
            placeholder="Search by name"
            value={search}
            onChange={handleSearchChange}
          />
          <WithAuthorization authorizedRoles={['admin', 'editor']}>
            <div className="flex items-center">
              <div className="inline-flex items-center border rounded-lg overflow-hidden">
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="rounded-r-none"
                >
                  <Plus className="h-4 w-4" />
                  Add recipe
                </Button>
              </div>
            </div>
          </WithAuthorization>
        </div>
      </ViewHeader>
      <ViewContent>
        <Suspense
          fallback={<DataTable table={table} loading={true} skeletonRows={5} />}
        >
          <RecipesTable searchQuery={search} selectedTags={tags} />
        </Suspense>
      </ViewContent>
      <AddRecipeModal open={showAddModal} onOpenChange={setShowAddModal} />
    </>
  );
}

const recipeListSearchSchema = z.object({
  search: z.string().default('').catch(''),
  tags: z.array(z.string()).default([]).catch([]),
});

export const Route = createFileRoute('/_authenticated/$tenantId/recipes/')({
  validateSearch: recipeListSearchSchema,
  component: RecipesRouteComponent,
});
